<template>
    <div id="app" :class="[`layout-${displayMode}`, {'app-ready': $store.state.applicationReady, 'accessibility-wheelchair': $store.state.accessibility.wheelchair}]">
        <keep-alive>
            <router-view v-if="$store.state.applicationReady"/>
        </keep-alive>

        <div v-if="$store.state.accessibility.wheelchair && cmsWebsite" class="wheelchair-placeholder">
            <b-img :src="cmsWebsite?.accessibility.wheelchair.placeholder.src"/>

            <div class="panel-title">
                <div class="panel-container">
                    <i class="fa-wheelchair fa-2x mr-2 float-left" :class="fontAwesomePrefix"/>
                    <h3 class="" v-tr>Accessible Mode|Mode accessible</h3>

                    <b-btn class="ml-3" variant="light" pill @click="$store.commit(`setAccessibilityWheelchair`, false)">
                        <span v-tr>Exit|Quitter</span>
                        <i class="fa-close ml-1" :class="fontAwesomePrefix"/>
                    </b-btn>
                </div>
            </div>
        </div>

        <!--        <e-loading-animation v-if="!$store.state.applicationReady"/>-->
        <b-progress class="website-loading" value="100" animated v-if="$store.state.applicationLoading"/>

        <e-mobile-modal v-if="isKioskDevice"
                        v-model="idleModal.visible"
                        class="idle-timeout-modal"
                        :close-on-backdrop="false"
                        :close-on-escape="false"
                        :hide-close="true"
                        :aria-label="$t(`map.idleTimeout.ariaLabel`)"
        >
            <div class="text-center py-3">
                <i class="far fa-timer fa-5x mb-3"/>
                <h1>{{ $t(`map.idleTimeout.title`) }}</h1>
                <p class="p1">{{ $t(`map.idleTimeout.description`) }}</p>
            </div>

            <template #footer>
                <div class="d-flex w-100 flex-column">
                    <b-btn variant="primary" size="lg" class="pt-3 pb-2" block @click="resetIdleTimeout" style="border-bottom-left-radius: 0; border-bottom-right-radius: 0">
                        <span class="p1">{{ $t(`map.idleTimeout.yes`) }} ({{ idleModal.timeRemaining }})</span>
                    </b-btn>

                    <b-progress class="" style="border-top-left-radius: 0; border-top-right-radius: 0" height="8px">
                        <b-progress-bar variant="secondary" :value="idleModal.timeRemaining / idleModal.duration * 100"/>
                    </b-progress>
                </div>
            </template>
        </e-mobile-modal>

        <e-mobile-modal v-if="!isKioskDevice"
                        v-model="cookieConsentModalVisible"
                        class="cookie-consent-modal"
                        :close-on-backdrop="false"
                        :close-on-escape="false"
                        :hide-close="true"
                        :aria-label="$t(`map.cookieConsent.ariaLabel`)"
        >
            <template #header>
                <div class="cookie-consent-header">
                    <div class="cookie-consent-header-title-container">
                        <i class="far fa-cookie-bite"/>
                        <div class="cookie-consent-header-title">{{ $t(`map.cookieConsent.title`) }}</div>
                    </div>
                    <div class="cookie-consent-header-description">{{ $t(`map.cookieConsent.description`) }}</div>
                </div>
            </template>

            <div v-if="cookieConsentStep === `main`" class="cookie-consent-body">
                <div class="cookie-consent-card">
                    <div class="cookie-consent-card-copy">
                        <div class="cookie-consent-card-title">{{ $t(`map.cookieConsent.necessary.title`) }}</div>
                        <div class="cookie-consent-card-description">{{ $t(`map.cookieConsent.necessary.description`) }}</div>
                    </div>
                    <span class="cookie-consent-badge">{{ $t(`map.cookieConsent.alwaysOn`) }}</span>
                </div>

                <div class="cookie-consent-card">
                    <div class="cookie-consent-card-copy">
                        <div class="cookie-consent-card-title">{{ $t(`map.cookieConsent.analytics.title`) }}</div>
                        <div class="cookie-consent-card-description">{{ $t(`map.cookieConsent.analytics.description`) }}</div>
                    </div>
                    <span class="cookie-consent-badge cookie-consent-badge-optional">{{ $t(`map.cookieConsent.optional`) }}</span>
                </div>
            </div>

            <div v-else class="cookie-consent-body">
                <div class="cookie-consent-card">
                    <div class="cookie-consent-card-copy">
                        <div class="cookie-consent-card-title">{{ $t(`map.cookieConsent.necessary.title`) }}</div>
                        <div class="cookie-consent-card-description">{{ $t(`map.cookieConsent.necessary.description`) }}</div>
                    </div>
                    <span class="cookie-consent-badge">{{ $t(`map.cookieConsent.alwaysOn`) }}</span>
                </div>

                <div class="cookie-consent-card cookie-consent-card-toggle">
                    <div class="cookie-consent-card-copy">
                        <div class="cookie-consent-card-title">{{ $t(`map.cookieConsent.analytics.title`) }}</div>
                        <div class="cookie-consent-card-description">{{ $t(`map.cookieConsent.analytics.customDescription`) }}</div>
                    </div>
                    <b-checkbox v-model="cookieConsentAnalyticsDraft" switch size="lg"/>
                </div>
            </div>

            <template #footer>
                <b-btn v-if="cookieConsentStep === `main`" variant="light" size="lg" block @click="cookieConsentStep = `custom`">
                    <span>{{ $t(`map.cookieConsent.customize`) }}</span>
                </b-btn>
                <b-btn v-else variant="light" size="lg" block @click="cookieConsentStep = `main`">
                    <i class="far fa-arrow-left mr-1"/>
                    <span>{{ $t(`map.cookieConsent.back`) }}</span>
                </b-btn>

                <b-btn v-if="cookieConsentStep === `custom`" variant="accent" size="lg" block @click="saveCookieConsentSelection">
                    <span>{{ $t(`map.cookieConsent.saveSelection`) }}</span>
                </b-btn>
                <b-btn v-else variant="accent" size="lg" block @click="acceptAllCookieConsent">
                    <span>{{ $t(`map.cookieConsent.acceptAll`) }}</span>
                </b-btn>

                <b-btn v-if="cookieConsentStep === `custom`" variant="outline-secondary" size="lg" block @click="acceptAllCookieConsent">
                    <span>{{ $t(`map.cookieConsent.acceptAll`) }}</span>
                </b-btn>
            </template>
        </e-mobile-modal>

        <modal-select-language/>
        <e-env-banner/>
    </div>
</template>

<script>
import ELoadingAnimation from "@vue-components/components/e-loading-animation.vue";
import {getLanguage, setLocale, translate} from "@vue-components/helpers/I18nPluginHelper.js"
import SessionTracker from "@/helpers/SessionTracker.js";
import EEnvBanner from "../vue-components/components/environment/e-env-banner.vue";
import ModalSelectLanguage from "@components/modal-select-language.vue";
import EMobileModal from "@components/e-mobile-modal.vue";
import {swal} from "vue-swal";

const COOKIE_CONSENT_KEYS = {
    legacyAccepted: `cookiesAccepted`,
    decided: `cookieConsentDecided`,
    analytics: `cookieConsentAnalytics`,
    functional: `cookieConsentFunctional`,
    editorAcknowledged: `editorCookiesAcknowledged`
};

const analyticsInitState = {
    gtagBootstrapped: false,
    gtagConfiguredIds: [],
    metaPixelBootstrapped: false,
    metaPixelInitializedIds: []
};

// app.vue #hot-reload-debug
export default {
    name: `App`,
    components: {EMobileModal, ModalSelectLanguage, EEnvBanner, ELoadingAnimation},
    metaInfo() {
        if (this.cmsWebsite) {
            return {
                title: translate(`Eye-In CMS|Editeur CMS`),
                titleTemplate: `%s | ${this.$tr(this.cmsWebsite.meta.title)}`
            }
        } else {
            return null;
        }
    },
    data() {
        return {
            idleTimeout: null,
            idleModal: {
                visible: false,
                duration: 10,
                timeRemaining: 0,
                interval: null,
            },
            ready: true, // True by default except on the login vue
            cookieConsentModalVisible: false,
            cookieConsentStep: `main`,
            cookieConsentAnalyticsDraft: false,
            cookieConsent: {
                decided: false,
                analytics: false,
                functional: true
            },
            editorCookiesAcknowledged: false
        };
    },
    computed: {
        cssVar() {
            let variables = {
                wheelchair: {
                    topMargin: this.cmsWebsite ? this.cmsWebsite.accessibility.wheelchair.topMargin : `800px`,
                    background: this.cmsWebsite ? this.cmsWebsite.getColor(this.cmsWebsite.accessibility.wheelchair.background) : `#000`,
                }
            };
            return variables;
        },
        isUserConnected() {
            return this.ready === true && this.$store.getters.isAuthenticated;
        },
        isEmbed() {
            return this.$route.query.embed === `true`;
        },
        isKioskMode() {
            return this.$store.state.isKioskDevice;
        },
        isRichCookieConsentRoute() {
            return !this.isKioskMode && [`cms-page-renderer`, `map-preview`, `web-preview`].includes(this.$route.name);
        },
        isEditorCookieToastRoute() {
            const path = this.$route.path;
            return !this.isKioskMode && path.startsWith(`/cms/`) && ![`map-preview`, `web-preview`].includes(this.$route.name);
        }
    },
    watch: {
        $route() {
            this.syncCookieExperience();
        },
        cmsWebsite() {
            this.maybeInitializeThirdPartyAnalytics();
        }
    },
    async created() {
        // don't use $route here
        const urlParams = new URLSearchParams(window.location.search);

        // Use store because session can't be created here, we need to wait for the cms-page-renderer
        // Since there might be a redirection don't put this code in cms-page-renderer

        if (parent !== window) {
            this.$store.state.sessionData.medium = `iframe`;
            this.$store.state.sessionData.source = document.referrer;
        }

        // Google Standard UTM Parameters
        this.$store.state.sessionData.medium = urlParams.get(`medium`);
        this.$store.state.sessionData.source = urlParams.get(`source`);
        this.$store.state.sessionData.campaign = urlParams.get(`campaign`);

        this.$store.state.sessionData.utms.utm_medium = urlParams.get(`utm_medium`);
        this.$store.state.sessionData.utms.utm_source = urlParams.get(`utm_source`);
        this.$store.state.sessionData.utms.utm_campaign = urlParams.get(`utm_campaign`);
        this.$store.state.sessionData.utms.utm_term = urlParams.get(`utm_term`);
        this.$store.state.sessionData.utms.utm_content = urlParams.get(`utm_content`);

        // Advanced params
        if (urlParams.get(`display`) === `kiosk` || localStorage.getItem(`kiosk`) === `yes`) {
            this.$store.state.isKioskDevice = true;
            window.isKioskDevice = true;
            localStorage.setItem(`kiosk`, `yes`);

            this.$store.state.idleTimeout = parseInt(urlParams.get(`timeout`) || localStorage.getItem(`idleTimeout`));
            if (this.$store.state.idleTimeout) {
                localStorage.setItem(`idleTimeout`, this.$store.state.idleTimeout);
            }

            this.$store.state.playerId = urlParams.get(`playerId`) || urlParams.get(`player`) || localStorage.getItem(`playerId`);
            if (this.$store.state.playerId) {
                localStorage.setItem(`playerId`, this.$store.state.playerId);
                this.$store.state.sessionData.medium = `player`;
                this.$store.state.sessionData.source = this.$store.state.playerId;
            }
        } else {
            window.isKioskDevice = false;
        }

        if (urlParams.get(`qr`)) {
            localStorage.setItem(`qrCode`, urlParams.get(`qr`));
            this.$store.state.sessionData.medium = `qr-code`;
            this.$store.state.sessionData.source = urlParams.get(`qr`);
        }

        if (urlParams.get(`iframe`) === `website`) {
            this.$store.state.sessionData.medium = `iframe`;
            this.$store.state.sessionData.source = `www.ciusss-ouestmtl.gouv.qc.ca`;
            this.persistRichCookieConsent(true);
            localStorage.setItem(COOKIE_CONSENT_KEYS.legacyAccepted, `true`);
        }

        if (urlParams.get(`lang`)) {
            this.$store.state.urlLanguage = urlParams.get(`lang`);
        }

        this.onWindowResize();
        window.addEventListener(`resize`, this.onWindowResize);
    },
    mounted() {
        if (this.isKioskDevice && this.$store.state.idleTimeout) {
            window.addEventListener(`click`, this.resetIdleTimeout.bind(this));
            window.addEventListener(`focus`, this.resetIdleTimeout.bind(this));
            this.resetIdleTimeout();
        }

        window.openCookieSettings = () => this.openCookieSettings();
        this.syncCookieExperience();
    },
    beforeDestroy() {
        this.hideEditorCookieToast();
        if (window.openCookieSettings) {
            delete window.openCookieSettings;
        }
        SessionTracker.clean();
    },
    methods: {
        translate,
        onWindowResize() {
            this.$store.state.isMobileLayout = window.isMobileLayout;
            this.$store.state.isSmallLayout = window.isSmallLayout;
            this.$store.state.isTabletLayout = window.isTabletLayout;
            this.$store.state.isMobileOrTabletLayout = window.isMobileOrTabletLayout;
            this.$store.state.isLargeLayout = window.isLargeLayout;
            this.$store.state.isExtraLargeLayout = window.isExtraLargeLayout;
            this.$store.commit(`updateDisplayMode`);
        },
        syncCookieExperience() {
            this.migrateLegacyCookieConsent();
            this.refreshRichCookieConsentState();
            this.refreshEditorCookieToastState();
            this.hideEditorCookieToast();

            if (this.isRichCookieConsentRoute) {
                if (!this.cookieConsent.decided) {
                    this.openCookieConsentModal(`main`, {analytics: false});
                } else {
                    this.cookieConsentModalVisible = false;
                    this.maybeInitializeThirdPartyAnalytics();
                }
                return;
            }

            this.cookieConsentModalVisible = false;

            if (this.isEditorCookieToastRoute && !this.editorCookiesAcknowledged) {
                this.showEditorCookieToast();
            }
        },
        migrateLegacyCookieConsent() {
            const hasLegacyConsent = localStorage.getItem(COOKIE_CONSENT_KEYS.legacyAccepted) === `true`;
            const hasNewDecision = localStorage.getItem(COOKIE_CONSENT_KEYS.decided) !== null;

            if (hasLegacyConsent && !hasNewDecision) {
                this.persistRichCookieConsent(true);
            }
        },
        refreshRichCookieConsentState() {
            this.cookieConsent.decided = localStorage.getItem(COOKIE_CONSENT_KEYS.decided) === `true`;
            this.cookieConsent.analytics = localStorage.getItem(COOKIE_CONSENT_KEYS.analytics) === `true`;
            this.cookieConsent.functional = localStorage.getItem(COOKIE_CONSENT_KEYS.functional) !== `false`;
            this.$store.commit(`setCookieConsent`, {
                decided: this.cookieConsent.decided,
                analytics: this.cookieConsent.analytics,
                functional: this.cookieConsent.functional
            });
        },
        refreshEditorCookieToastState() {
            this.editorCookiesAcknowledged = localStorage.getItem(COOKIE_CONSENT_KEYS.editorAcknowledged) === `true`;
        },
        openCookieConsentModal(step = `custom`, defaults = {}) {
            if (!this.isRichCookieConsentRoute) {
                return;
            }

            this.cookieConsentStep = step;
            this.cookieConsentAnalyticsDraft = typeof defaults.analytics === `boolean` ? defaults.analytics : this.cookieConsent.analytics;
            this.cookieConsentModalVisible = true;
        },
        openCookieSettings() {
            this.refreshRichCookieConsentState();
            this.openCookieConsentModal(`custom`, {
                analytics: this.cookieConsent.analytics
            });
        },
        persistRichCookieConsent(analytics) {
            localStorage.setItem(COOKIE_CONSENT_KEYS.decided, `true`);
            localStorage.setItem(COOKIE_CONSENT_KEYS.analytics, analytics ? `true` : `false`);
            localStorage.setItem(COOKIE_CONSENT_KEYS.functional, `true`);

            this.cookieConsent.decided = true;
            this.cookieConsent.analytics = analytics;
            this.cookieConsent.functional = true;
            this.$store.commit(`setCookieConsent`, {
                decided: true,
                analytics,
                functional: true
            });
        },
        acceptAllCookieConsent() {
            this.persistRichCookieConsent(true);
            this.cookieConsentModalVisible = false;
            this.maybeInitializeThirdPartyAnalytics();
        },
        saveCookieConsentSelection() {
            this.persistRichCookieConsent(this.cookieConsentAnalyticsDraft);
            this.cookieConsentModalVisible = false;
            this.maybeInitializeThirdPartyAnalytics();
        },
        showEditorCookieToast() {
            const h = this.$createElement;
            const vNodesMsg = h(
                `div`, {class: [`cookie-toast-msg d-flex flex-column`]},
                [
                    h(`span`, {class: [`mb-2`]}, translate(`Cookies help us remember essential website settings while you work in the CMS.| Les cookies nous aident à mémoriser les réglages essentiels du site pendant que vous travaillez dans le CMS.`)),
                    h(`div`, {class: [`d-flex`]},
                        [
                            h(`b-btn`, {class: [`ml-auto`], props: {variant: `primary`}, on: {click: this.acknowledgeEditorCookies}}, translate(`Accept | Accepter`)),
                        ]
                    )
                ]
            );
            const vNodesTitle = h(
                `div`,
                {class: [`cookie-toast-title`]},
                [h(`i`, {class: [`far fa-cookie-bite mr-2`]}), h(`strong`, translate(`Cookie Notice | Avis relatif aux cookies`))]
            );
            this.$bvToast.toast([vNodesMsg], {
                id: `editor-cookie-toast`,
                title: [vNodesTitle],
                solid: true,
                variant: `info`,
                noAutoHide: true,
                toaster: `b-toaster-bottom-right`,
            });
        },
        hideEditorCookieToast() {
            this.$bvToast.hide(`editor-cookie-toast`);
        },
        acknowledgeEditorCookies() {
            this.hideEditorCookieToast();
            this.editorCookiesAcknowledged = true;
            localStorage.setItem(COOKIE_CONSENT_KEYS.editorAcknowledged, `true`);
        },
        maybeInitializeThirdPartyAnalytics() {
            if (!this.isRichCookieConsentRoute || !this.cookieConsent.analytics) {
                return;
            }

            if (!this.cmsWebsite || !this.cmsWebsite.settings || !this.cmsWebsite.settings.analytics) {
                return;
            }

            const googleTags = this.cmsWebsite.settings.analytics.googleTags && this.cmsWebsite.settings.analytics.googleTags.trim();
            const metaPixel = this.cmsWebsite.settings.analytics.metaPixel && this.cmsWebsite.settings.analytics.metaPixel.trim();

            if (googleTags) {
                this.initializeGoogleTag(googleTags);
            }

            if (metaPixel) {
                this.initializeMetaPixel(metaPixel);
            }
        },
        initializeGoogleTag(tagId) {
            window.dataLayer = window.dataLayer || [];
            window.gtag = window.gtag || function() {
                window.dataLayer.push(arguments);
            };

            if (!document.getElementById(`google-tag-manager`)) {
                const script = document.createElement(`script`);
                script.id = `google-tag-manager`;
                script.async = true;
                script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`;
                document.head.appendChild(script);
            }

            if (!analyticsInitState.gtagBootstrapped) {
                window.gtag(`js`, new Date());
                analyticsInitState.gtagBootstrapped = true;
            }

            if (!analyticsInitState.gtagConfiguredIds.includes(tagId)) {
                window.gtag(`config`, tagId);
                analyticsInitState.gtagConfiguredIds.push(tagId);
            }
        },
        initializeMetaPixel(pixelId) {
            if (!analyticsInitState.metaPixelBootstrapped) {
                !function(f, b, e, v, n, t, s) {
                    if (f.fbq) return;
                    n = f.fbq = function() {
                        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
                    };
                    if (!f._fbq) f._fbq = n;
                    n.push = n;
                    n.loaded = true;
                    n.version = `2.0`;
                    n.queue = [];
                    t = b.createElement(e);
                    t.async = true;
                    t.src = v;
                    t.id = `meta-pixel-script`;
                    s = b.getElementsByTagName(e)[0];
                    s.parentNode.insertBefore(t, s);
                }(window, document, `script`, `https://connect.facebook.net/en_US/fbevents.js`);

                analyticsInitState.metaPixelBootstrapped = true;
            }

            if (!analyticsInitState.metaPixelInitializedIds.includes(pixelId) && window.fbq) {
                window.fbq(`init`, pixelId);
                window.fbq(`track`, `PageView`);
                analyticsInitState.metaPixelInitializedIds.push(pixelId);
            }
        },
        resetIdleTimeout() {
            // TODO move all this part into a dedicated component
            // console.log(`Idle Timeout Check ${this.$store.state.idleTimeout}s`);

            this.idleModal.visible = false;
            if (this.idleTimeout) {
                clearTimeout(this.idleTimeout);
            }
            if (this.idleModal.interval) {
                clearInterval(this.idleModal.interval);
            }
            this.idleTimeout = setTimeout(() => {
                this.idleModal.visible = true;
                this.idleModal.timeRemaining = this.idleModal.duration;
                this.idleModal.interval = setInterval(() => {
                    if (this.idleModal.timeRemaining <= 0) {
                        this.showIdleScreen();
                    } else {
                        this.idleModal.timeRemaining--;
                    }
                }, 1000);
            }, this.$store.state.idleTimeout * 1000);
        },
        showIdleScreen() {
            if (this.idleModal.interval) {
                clearInterval(this.idleModal.interval);
            }
            // console.log(`Show Idle Screen`);

            if (window.parent) {
                console.log(`window.parent found`);
                window.parent.postMessage({
                    type: `widget`,
                    action: `show-idle-screen`
                }, `*`);
            } else {
                console.warn(`window.parent NOT found`);
                if (window.top) {
                    console.log(`window.top found`);
                    window.top.postMessage({
                        type: `widget`,
                        action: `show-idle-screen`
                    }, `*`);
                } else {
                    console.warn(`window.top NOT found`);
                }
            }

            if (this.$route.path === `/` && this.language === `fr`) {
                // console.log(`Already on home page, no need to reset page`);
                if (this.isWheelchairMode) {
                    this.$store.commit(`setAccessibilityWheelchair`, false);
                }
                this.resetIdleTimeout();
            } else {
                // console.log(`Reset to default page`);
                setLocale(`fr`);
                window.history.pushState({}, ``, `/`);
                window.location.reload();
            }
        }
    }
}
</script>

<style lang="scss">
@import "assets/style/global.scss";

html {
    height: var(--app-height);
}

body {
    width: 100%;
    height: 100vh;
    height: var(--app-height);
    padding: 0;
    font-family: var(--body-font);
    position: relative;
    overflow: hidden auto;
    container-type: inline-size;
}

#app {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #2c3e50;
    width: 100%;
    height: var(--app-height);
    --wheelchair-top-margin: v-bind('cssVar.wheelchair.topMargin');

    &.accessibility-wheelchair {
        padding-top: var(--wheelchair-top-margin) !important;
        --app-height: calc(100vh - var(--wheelchair-top-margin)) !important;
    }
}

.wheelchair-placeholder {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: var(--wheelchair-top-margin);
    z-index: 0;
    overflow: hidden;

    img {
        width: 100%;
        height: calc(100% - 100px);
        object-fit: cover;
    }

    .panel-title {
        width: 100%;
        height: 100px;
        padding: 25px;
        display: flex;
        justify-content: center;
        align-items: center;
        background: v-bind('cssVar.wheelchair.background');

        .panel-container {
            display: flex;
            justify-content: center;
            align-items: center;
            background: white;
            border-radius: 50px;
            padding: 10px 10px 10px 20px;
        }

        .btn {
            padding: 8px 20px;
            font-size: 15pt;
            height: 50px;
        }
    }
}

.progress.website-loading {
    position: fixed;
    height: 3px;
    top: 0;
    left: 0;
    right: 0;
    z-index: 2000;
    border-radius: 0;
}

.cookie-consent-modal {
    ::v-deep .e-mobile-modal__panel {
        border-radius: 24px;
    }

    ::v-deep .e-mobile-modal__header {
        padding: 0;
    }

    ::v-deep .e-mobile-modal__body {
        padding-top: 8px;
    }
}

.cookie-consent-header {
    display: flex;
    flex-direction: column;
    gap: 8px;
    text-align: left;
    padding-top: 8px;
}

.cookie-consent-header-title-container {
    display: flex;
    gap: 8px;
    align-items: center;
    font-size: 20pt;
    flex-shrink: 0;

    i {
        color: var(--color-primary, #3b82f6);
    }
}

.cookie-consent-header-title {
    font-size: 16pt;
    font-weight: 700;
    line-height: 1.2;
    text-wrap: balance;
}

.cookie-consent-header-description {
    font-size: 10.5pt;
    color: #5f6b7a;
    line-height: 1.45;
    text-wrap: pretty;
}

.cookie-consent-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.cookie-consent-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 16px;
    border-radius: 18px;
    background: #f7f8fa;
    border: 1px solid rgba(34, 41, 47, 0.08);
}

.cookie-consent-card-toggle {
    align-items: flex-start;
}

.cookie-consent-card-copy {
    flex: 1 1 auto;
    text-align: left;
}

.cookie-consent-card-title {
    font-size: 11pt;
    font-weight: 700;
    margin-bottom: 4px;
}

.cookie-consent-card-description {
    font-size: 10pt;
    line-height: 1.4;
    color: #66717f;
}

.cookie-consent-badge {
    flex-shrink: 0;
    white-space: nowrap;
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(34, 197, 94, 0.12);
    color: #0f7b35;
    font-size: 9pt;
    font-weight: 700;
}

.cookie-consent-badge-optional {
    background: rgba(59, 130, 246, 0.12);
    color: #215cb8;
}
</style>
